/**
 * CheckAddFormBody — orchestrator for the `add-check-constraint` form.
 * Owns state + commit dispatch; renders name + expression + the per-engine
 * extras via `CheckAddEngineFields` (split per the 200-line file budget).
 *
 * Engine-aware behaviour:
 *   - sqlite: caller (`CheckAddPopover`) gates rendering with
 *     `isAddCheckConstraintSupported`; included as a defensive runtime guard.
 *   - mssql: WITH NOCHECK checkbox.
 *   - postgres: NO INHERIT checkbox.
 *   - mysql: NOT ENFORCED checkbox + 8.0.16+ version note.
 *   - oracle: DEFERRABLE radios.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B4.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import type { CheckConstraintDef } from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAddCheckConstraint,
  getCheckEngineVersionNote,
  isDeferrableSupported,
  isMssqlWithNoCheckSupported,
  isMysqlNotEnforcedSupported,
  isPgsqlNoInheritSupported,
  validateCheckConstraintForEngine,
} from '../../edit/edits/addCheckConstraint';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  CheckAddEngineFields,
  type DeferrableMode,
} from './CheckAddEngineFields';
import { FormEngineChip } from './FormEngineChip';

export interface CheckAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function CheckAddFormBody(props: CheckAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showWithNoCheck = isMssqlWithNoCheckSupported(engine);
  const showNoInherit = isPgsqlNoInheritSupported(engine);
  const showNotEnforced = isMysqlNotEnforcedSupported(engine);
  const showDeferrable = isDeferrableSupported(engine);
  const mysqlVersionNote = getCheckEngineVersionNote(engine);

  const [expression, setExpression] = useState('');
  const [name, setName] = useState('');
  const [withNoCheck, setWithNoCheck] = useState(false);
  const [noInherit, setNoInherit] = useState(false);
  const [notEnforced, setNotEnforced] = useState(false);
  const [deferrable, setDeferrable] = useState<DeferrableMode>('unspecified');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildCheck(): CheckConstraintDef | { error: string } {
    const draft: {
      name?: string;
      expression: string;
      mssqlWithNoCheck?: boolean;
      pgsqlNoInherit?: boolean;
      mysqlNotEnforced?: boolean;
      deferrable?: 'deferred' | 'immediate';
    } = { expression };
    const trimmedName = name.trim();
    if (trimmedName !== '') draft.name = trimmedName;
    if (showWithNoCheck && withNoCheck) draft.mssqlWithNoCheck = true;
    if (showNoInherit && noInherit) draft.pgsqlNoInherit = true;
    if (showNotEnforced && notEnforced) draft.mysqlNotEnforced = true;
    if (showDeferrable && deferrable !== 'unspecified') draft.deferrable = deferrable;
    const err = validateCheckConstraintForEngine(engine, draft);
    if (err !== null) return { error: err };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    const built = buildCheck();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddCheckConstraint(editContext, { check: built });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add check constraint</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-expression`} className="text-xs">
          Expression <span className="text-destructive">*</span>
        </Label>
        <textarea
          id={`${testId}-expression`}
          data-testid={`${testId}-expression`}
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          placeholder="price >= 0"
          className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
        />
        <span className="text-[10px] text-muted-foreground">
          Verbatim expression text — the synthesizer wraps it in parentheses.
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">Name (optional)</Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ck_table_price"
          className="h-8 font-mono text-xs"
        />
      </div>

      <CheckAddEngineFields
        testId={testId}
        showWithNoCheck={showWithNoCheck}
        showNoInherit={showNoInherit}
        showNotEnforced={showNotEnforced}
        showDeferrable={showDeferrable}
        mysqlVersionNote={mysqlVersionNote}
        withNoCheck={withNoCheck}
        noInherit={noInherit}
        notEnforced={notEnforced}
        deferrable={deferrable}
        setWithNoCheck={setWithNoCheck}
        setNoInherit={setNoInherit}
        setNotEnforced={setNotEnforced}
        setDeferrable={setDeferrable}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
